import { useContext, useCallback, useEffect, useMemo } from "react";
import { useWatch, Controller } from "react-hook-form";
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import pt from "date-fns/locale/pt";
import { GroupsTitle } from "../../components/groups/GroupsTitle";
import { RecordContext } from "../../components/record/Record";
import { RelatedInputField } from "../../components/fieldsInputs/fields/RelatedInputField/RelatedInputField";
import { renderFieldInput } from "../../components/fieldsInputs/renderFieldInput";
import { useRecords } from "../../api/useRecords";
import { useServiceTimes } from "../../api/useServiceTimes";
import { NFB_SERVICE, GENERAL_EEG_SERVICE, FIRST_EEG_SERVICE } from "../../utils/constants";
import { useSessionTimeOptions } from "../sessions/components/session-form/useSessionTimeOptions";
import {
    DEFAULT_TIME,
    toDate,
    combineDateAndTime,
    parseTimeString,
    extractTimeFromDate,
    buildTimeOptions,
    isTimeValid,
} from "../sessions/components/session-form/sessionFormUtils";

const BRAIN_MAPS_TYPE = "brain_maps";

const CLINIC_EXTRA_OPTIONS = {
    enabled: true,
    displayTemplate: "{{name}}",
    fieldsQuery: "id,name",
    sort: "name",
};

const SCHEDULE_FIELDS_QUERY =
    "id,start_date,end_date,user.id,user.first_name,user.last_name,user.avatar,user.technician_id.specializations";

const scheduleMatchesSpecialization = (scheduleItem: any, specialization: string) => {
    const matches = scheduleItem.user?.technician_id?.some((technician: any) =>
        technician?.specializations?.includes(specialization)
    );
    return matches;
};

export const GapPriorityFormContent = () => {
    const recordContext = useContext(RecordContext);
    const entityForm = recordContext?.entityForm;
    const item = recordContext?.item;
    const formatedFields = recordContext?.formatedFields;

    const { data: servicesData } = useRecords("services", "id,name,max_duration", {
        staleTime: Infinity,
        extraOptions: { sort: "sort_order", filter: { sort_order: { _nnull: true } } },
    });
    const { data: serviceTimesData } = useServiceTimes();

    const watchedStartDate = useWatch({ control: entityForm?.control, name: "start_date" });
    const watchedEndDate = useWatch({ control: entityForm?.control, name: "end_date" });
    const watchedClinic = useWatch({ control: entityForm?.control, name: "clinic" });
    const watchedType = useWatch({ control: entityForm?.control, name: "type" });
    const currentStartDate = watchedStartDate ?? item?.start_date;
    const currentEndDate = watchedEndDate ?? item?.end_date;
    const currentClinic = watchedClinic ?? item?.clinic;
    const currentClinicId = currentClinic?.id || currentClinic;
    const currentType = watchedType ?? item?.type;
    const isBrainMaps = currentType === BRAIN_MAPS_TYPE;

    const { allowedTimes, currentTime, isTimeInvalid, timeOptions } = useSessionTimeOptions({
        currentServiceId: NFB_SERVICE,
        currentStartDate,
        serviceTimesData,
        servicesItems: servicesData?.items,
    });

    const endTime = extractTimeFromDate(currentEndDate);
    const endTimeOptions = buildTimeOptions(allowedTimes, endTime);
    const isEndTimeInvalid = !isTimeValid(allowedTimes, endTime);

    // only schedules that cover the selected interval, on the selected clinic, with NFB/EEG services
    // and whose technician has the specialization selected in `type`
    const technicianScheduleExtraOptions = useMemo(() => {
        const startDate = toDate(currentStartDate);
        const endDate = toDate(currentEndDate);
        // fieldsQuery must be present even while disabled: RelatedInputField snapshots it on mount
        // brain maps gaps are not clinic/schedule-specific, so the picker stays disabled
        if (isBrainMaps || !startDate || !endDate || !currentClinicId || !currentType) {
            return { enabled: false, fieldsQuery: SCHEDULE_FIELDS_QUERY };
        }

        const extraOptions = {
            enabled: true,
            displayTemplate: "{{user.first_name}} {{user.last_name}}",
            fieldsQuery: SCHEDULE_FIELDS_QUERY,
            sort: "user.first_name",
            // Directus cannot filter json fields server-side, so the specialization filter is
            // applied here; `specialization` keeps the query key distinct per selected type
            specialization: currentType,
            manipulateResult: (items: Array<any>) =>
                items
                    .filter((scheduleItem) => scheduleMatchesSpecialization(scheduleItem, currentType))
                    // the option/input avatar rendering expects the user under `user_id`
                    .map((scheduleItem) => ({ ...scheduleItem, user_id: scheduleItem.user })),
            filter: {
                _and: [
                    { start_date: { _lt: endDate.toISOString() } },
                    { end_date: { _gt: startDate.toISOString() } },
                    { clinic_id: { id: { _eq: currentClinicId } } },
                    { services: { services_id: { id: { _in: [NFB_SERVICE, GENERAL_EEG_SERVICE, FIRST_EEG_SERVICE] } } } },
                ],
            },
        };
        return extraOptions;
    }, [currentStartDate, currentEndDate, currentClinicId, currentType, isBrainMaps]);

    // brain maps clears any previously picked schedule (clinic value is left untouched)
    useEffect(() => {
        if (!entityForm || !isBrainMaps) return;
        if (entityForm.getValues("technician_schedule")) {
            entityForm.setValue("technician_schedule", null, { shouldDirty: true });
        }
    }, [isBrainMaps, entityForm]);

    // same query key as the RelatedInputField below (shared cache, no extra request);
    // used only to show the "no technicians" hint, since the input's empty text is generic
    const { data: matchingSchedulesData, status: matchingSchedulesStatus } = useRecords(
        "schedules",
        SCHEDULE_FIELDS_QUERY,
        {
            enabled: technicianScheduleExtraOptions.enabled,
            extraOptions: technicianScheduleExtraOptions as any,
        }
    );
    const hasNoAvailableTechnicians =
        technicianScheduleExtraOptions.enabled &&
        matchingSchedulesStatus === "success" &&
        !(matchingSchedulesData?.items ?? []).some((scheduleItem: any) =>
            scheduleMatchesSpecialization(scheduleItem, currentType)
        );

    const handleDateChange = useCallback(
        (newDate: Date | null) => {
            if (!entityForm) return;

            if (!newDate) {
                entityForm.setValue("start_date", null, { shouldDirty: true });
                return;
            }

            const currentValue = entityForm.getValues("start_date");
            const existingDate = toDate(currentValue);
            const existingTime = existingDate
                ? { hours: existingDate.getHours(), minutes: existingDate.getMinutes() }
                : DEFAULT_TIME;

            const combinedDate = combineDateAndTime(newDate, existingTime.hours, existingTime.minutes);
            entityForm.setValue("start_date", combinedDate, { shouldDirty: true });

            const existingEndDate = toDate(entityForm.getValues("end_date"));
            if (existingEndDate) {
                const combinedEndDate = combineDateAndTime(newDate, existingEndDate.getHours(), existingEndDate.getMinutes());
                entityForm.setValue("end_date", combinedEndDate, { shouldDirty: true });
            }
        },
        [entityForm]
    );

    const handleTimeChange = useCallback(
        (timeString: string) => {
            if (!entityForm) return;

            const { hours, minutes } = parseTimeString(timeString);
            const currentValue = entityForm.getValues("start_date");
            const baseDate = toDate(currentValue) || new Date();

            const combinedDate = combineDateAndTime(baseDate, hours, minutes);
            entityForm.setValue("start_date", combinedDate, { shouldDirty: true });
        },
        [entityForm]
    );

    const handleEndTimeChange = useCallback(
        (timeString: string) => {
            if (!entityForm) return;

            const { hours, minutes } = parseTimeString(timeString);
            const existingEndDate = entityForm.getValues("end_date");
            const existingStartDate = entityForm.getValues("start_date");
            const baseDate = toDate(existingStartDate) || toDate(existingEndDate) || new Date();

            const combinedDate = combineDateAndTime(baseDate, hours, minutes);
            entityForm.setValue("end_date", combinedDate, { shouldDirty: true });
        },
        [entityForm]
    );

    if (!recordContext || !entityForm || !formatedFields) return null;

    const statusField = formatedFields.find((field) => field.field === "status");
    const typeField = formatedFields.find((field) => field.field === "type");
    const clinicField = formatedFields.find((field) => field.field === "clinic");
    const technicianScheduleField = formatedFields.find((field) => field.field === "technician_schedule");

    return (
        <Box sx={{ backgroundColor: "#fff" }}>
            <Box sx={{ px: 4, pt: 4, pb: 4 }}>
                <GroupsTitle title="Agendamento da Prioridade" />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pt}>
                <Box sx={{ px: 4, pb: 4, display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
                    <Box sx={{ flex: "2 1 340px", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                            {statusField ? (
                                <Box sx={{ minWidth: 160, flex: 1 }}>
                                    {renderFieldInput(statusField, item?.status, "group", entityForm)}
                                </Box>
                            ) : null}
                            {clinicField ? (
                                <Box sx={{ minWidth: 160, flex: 1 }}>
                                    {renderFieldInput(clinicField, item?.clinic || null, "group", entityForm, { ...CLINIC_EXTRA_OPTIONS, disabled: isBrainMaps })}
                                </Box>
                            ) : null}
                        </Box>

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ minWidth: 180, flex: 1 }}>
                        <Controller
                            name="start_date"
                            control={entityForm.control}
                            defaultValue={toDate(item?.start_date)}
                            render={({ field }) => (
                                <DatePicker
                                    label="Data"
                                    format="dd/MM/yyyy"
                                    value={toDate(field.value)}
                                    onChange={handleDateChange}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            variant: "outlined",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ minWidth: 120, flex: 0.5 }}>
                        <FormControl fullWidth variant="outlined" error={isTimeInvalid}>
                            <InputLabel>Hora Início</InputLabel>
                            <Select
                                value={currentTime}
                                label="Hora Início"
                                onChange={(e) => handleTimeChange(e.target.value)}
                            >
                                {timeOptions.map((time) => (
                                    <MenuItem
                                        key={time}
                                        value={time}
                                        sx={isTimeInvalid && time === currentTime ? { color: "error.main" } : {}}
                                    >
                                        {time}
                                    </MenuItem>
                                ))}
                            </Select>
                            {isTimeInvalid && (
                                <Box component="span" sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}>
                                    Hora inválida
                                </Box>
                            )}
                        </FormControl>
                    </Box>

                    <Box sx={{ minWidth: 120, flex: 0.5 }}>
                        <FormControl fullWidth variant="outlined" error={isEndTimeInvalid}>
                            <InputLabel>Hora Fim</InputLabel>
                            <Select
                                value={endTime}
                                label="Hora Fim"
                                onChange={(e) => handleEndTimeChange(e.target.value)}
                            >
                                {endTimeOptions.map((time) => (
                                    <MenuItem
                                        key={time}
                                        value={time}
                                        sx={isEndTimeInvalid && time === endTime ? { color: "error.main" } : {}}
                                    >
                                        {time}
                                    </MenuItem>
                                ))}
                            </Select>
                            {isEndTimeInvalid && (
                                <Box component="span" sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}>
                                    Hora inválida
                                </Box>
                            )}
                        </FormControl>
                    </Box>
                    </Box>
                    </Box>

                    <Box sx={{ flex: "1 1 220px", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                        {typeField ? (
                            <Box>{renderFieldInput(typeField, item?.type, "group", entityForm)}</Box>
                        ) : null}

                    {technicianScheduleField && (
                        <Box sx={{ minWidth: 220, flex: 1 }}>
                            <RelatedInputField
                                field={technicianScheduleField}
                                value={item?.technician_schedule || null}
                                fieldView="group"
                                entityForm={entityForm}
                                options={{ variant: "outlined", disabled: !technicianScheduleExtraOptions.enabled }}
                                extraOptions={technicianScheduleExtraOptions}
                            />
                            {hasNoAvailableTechnicians ? (
                                <Typography variant="caption" sx={{ color: "error.main" }}>
                                    Sem técnicos disponíveis neste horário
                                </Typography>
                            ) : null}
                        </Box>
                    )}
                    </Box>
                </Box>
            </LocalizationProvider>
        </Box>
    );
};
